import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  CheckCircle2, Circle, Loader2, XCircle, ChevronDown, ChevronUp,
  Play, Upload, CalendarClock, FolderOpen, RefreshCw, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StageRow = ({ stage }) => (
  <div className="flex items-center gap-2 text-sm py-0.5" data-testid={`stage-${stage.state}`}>
    {stage.state === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
    {stage.state === 'active' && <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />}
    {stage.state === 'pending' && <Circle className="w-4 h-4 text-zinc-600 shrink-0" />}
    {stage.state === 'failed' && <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
    <span className={stage.state === 'pending' ? 'text-zinc-500' : stage.state === 'failed' ? 'text-red-300' : 'text-zinc-200'}>
      {stage.label}
    </span>
  </div>
);

const PublishPanel = ({ artifact, token, onDone }) => {
  const [platform, setPlatform] = useState('youtube');
  const [privacy, setPrivacy] = useState('unlisted');
  const [busy, setBusy] = useState(false);

  const publish = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/social/publish`, {
        platform, content_id: artifact.id, content_type: 'video',
        title: (artifact.title || 'Delphi video').slice(0, 95),
        privacy, idempotency_key: `card-${artifact.id}-${platform}`,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.success) {
        toast.success(`Published to ${platform}${r.data.post_url ? ' — ' + r.data.post_url : ''}`);
        onDone && onDone(true);
      } else {
        toast.error(r.data?.error || 'Publishing failed');
        onDone && onDone(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publishing failed');
      onDone && onDone(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 p-3 rounded-lg bg-zinc-900/70 border border-border space-y-2" data-testid="publish-panel">
      <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Ready to publish</p>
      <div className="flex flex-wrap gap-2">
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
          className="bg-zinc-800 text-sm rounded-md px-2 py-1.5 border border-border" data-testid="publish-platform-select">
          <option value="youtube">YouTube</option>
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
          <option value="tiktok">TikTok</option>
        </select>
        <select value={privacy} onChange={(e) => setPrivacy(e.target.value)}
          className="bg-zinc-800 text-sm rounded-md px-2 py-1.5 border border-border" data-testid="publish-privacy-select">
          <option value="unlisted">Unlisted</option>
          <option value="private">Private</option>
          <option value="public">Public</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={publish} disabled={busy} className="h-8" data-testid="approve-publish-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          Approve & Publish
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDone && onDone(false)} className="h-8" data-testid="cancel-publish-btn">
          Cancel
        </Button>
      </div>
    </div>
  );
};

const SchedulePanel = ({ artifact, token, onDone }) => {
  const [platform, setPlatform] = useState('youtube');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);

  const schedule = async () => {
    if (!when) { toast.error('Pick a date and time first'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/scheduler/`, {
        content_id: artifact.id, content_type: 'video', platform,
        title: (artifact.title || 'Delphi video').slice(0, 95),
        scheduled_time: new Date(when).toISOString(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Scheduled for ${new Date(when).toLocaleString()}`);
      onDone && onDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scheduling failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 p-3 rounded-lg bg-zinc-900/70 border border-border space-y-2" data-testid="schedule-panel">
      <p className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Schedule this</p>
      <div className="flex flex-wrap gap-2">
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
          min={new Date(Date.now() + 10 * 60000).toISOString().slice(0, 16)}
          className="bg-zinc-800 text-sm rounded-md px-2 py-1.5 border border-border text-zinc-200"
          data-testid="schedule-datetime-input" />
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
          className="bg-zinc-800 text-sm rounded-md px-2 py-1.5 border border-border" data-testid="schedule-platform-select">
          <option value="youtube">YouTube</option>
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
          <option value="tiktok">TikTok</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={schedule} disabled={busy} className="h-8" data-testid="confirm-schedule-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CalendarClock className="w-3.5 h-3.5 mr-1" />}
          Schedule
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDone && onDone(false)} className="h-8" data-testid="cancel-schedule-btn">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const ActionRunCard = ({ runId, token, onFollowUp }) => {
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const timerRef = useRef(null);
  const [elapsed, setElapsed] = useState('');

  // Live "Creating for Xm Ys" from real timestamps
  useEffect(() => {
    if (!run || run.status !== 'running' || !run.started_at) { setElapsed(''); return; }
    const tick = () => {
      const s = Math.max(0, Math.floor((Date.now() - new Date(run.started_at).getTime()) / 1000));
      setElapsed(s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [run?.status, run?.started_at]);

  const fetchRun = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/delphi/runs/${runId}`, { headers: { Authorization: `Bearer ${token}` } });
      setRun(r.data);
      return r.data;
    } catch { return null; }
  }, [runId, token]);

  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      const data = await fetchRun();
      if (stopped) return;
      if (data && (data.status === 'running' || data.status === 'queued')) {
        timerRef.current = setTimeout(poll, 4000);
      }
    };
    poll();
    return () => { stopped = true; clearTimeout(timerRef.current); };
  }, [fetchRun]);

  if (!run) return null;

  const kindLabel = { reel: 'Video', image: 'Image', music: 'Music',
    voiceover: 'Voiceover', workforce_run: 'Task' }[run.artifact_type] || 'Job';

  const titleVerb = { reel: 'Creating your video', image: 'Creating your image',
    music: 'Composing your music', voiceover: 'Recording your voiceover',
    workforce_run: 'Agents working' }[run.artifact_type] || 'Working';

  return (
    <div className="mt-2 w-full max-w-md rounded-xl border border-border bg-zinc-900/60 p-3.5" data-testid="action-run-card">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-white truncate">
          {run.status === 'completed' ? '✓ Ready' : run.status === 'failed' ? 'Something went wrong' : titleVerb}
        </p>
        {run.status === 'running' && (
          <span className="text-xs text-primary shrink-0" data-testid="run-progress">
            {elapsed && <span className="text-zinc-500 mr-1.5" data-testid="run-elapsed">Creating for {elapsed}</span>}
            {run.progress}%
          </span>
        )}
      </div>
      {run.title && <p className="text-xs text-zinc-500 truncate mt-0.5">{run.title}</p>}

      {/* Progress bar */}
      {run.status === 'running' && (
        <div className="h-1 bg-zinc-800 rounded-full mt-2 overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${run.progress}%` }} />
        </div>
      )}

      {/* Stages */}
      {run.stages?.length > 0 && (
        <div className="mt-2">{run.stages.map((s, i) => <StageRow key={i} stage={s} />)}</div>
      )}

      {/* Failure */}
      {run.status === 'failed' && (
        <div className="mt-2">
          <p className="text-sm text-red-300" data-testid="run-error">{run.error || 'This didn\u2019t work.'}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {onFollowUp && (
              <Button size="sm" variant="outline" className="h-8" onClick={() => onFollowUp('Try again')} data-testid="retry-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Try Again
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Completed artifact */}
      {run.status === 'completed' && run.artifact && (
        <div className="mt-2">
          {run.artifact.type === 'reel' && run.artifact.url && (
            <>
              {showPreview ? (
                <video src={run.artifact.url} controls playsInline className="w-full max-h-72 rounded-lg bg-black" data-testid="video-preview" />
              ) : null}
              <div className="flex flex-wrap gap-2 mt-2">
                <Button size="sm" variant="outline" className="h-8" onClick={() => setShowPreview(!showPreview)} data-testid="preview-btn">
                  <Play className="w-3.5 h-3.5 mr-1" /> {showPreview ? 'Hide' : 'Preview'}
                </Button>
                <Button size="sm" className="h-8" onClick={() => setShowPublish(!showPublish)} data-testid="publish-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> Publish
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setShowSchedule(!showSchedule); setShowPublish(false); }} data-testid="schedule-btn">
                  <CalendarClock className="w-3.5 h-3.5 mr-1" /> Schedule
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => navigate('/library')} data-testid="library-btn">
                  <FolderOpen className="w-3.5 h-3.5 mr-1" /> Library
                </Button>
              </div>
              {showPublish && <PublishPanel artifact={run.artifact} token={token} onDone={() => setShowPublish(false)} />}
              {showSchedule && <SchedulePanel artifact={run.artifact} token={token} onDone={() => setShowSchedule(false)} />}
            </>
          )}
          {run.artifact.type === 'image' && run.artifact.url && (
            <>
              <img src={run.artifact.url} alt={run.artifact.title || 'Generated'} className="w-full max-h-72 object-contain rounded-lg bg-black" data-testid="image-preview" />
              <div className="flex flex-wrap gap-2 mt-2">
                {onFollowUp && (
                  <Button size="sm" variant="outline" className="h-8" onClick={() => onFollowUp('Try another version')} data-testid="regenerate-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate
                  </Button>
                )}
                <a href={run.artifact.url} download target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="h-8" data-testid="download-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                </a>
                <Button size="sm" variant="outline" className="h-8" onClick={() => navigate('/library')} data-testid="image-library-btn">
                  <FolderOpen className="w-3.5 h-3.5 mr-1" /> Library
                </Button>
              </div>
            </>
          )}
          {(run.artifact.type === 'music' || run.artifact.type === 'voiceover') && run.artifact.url && (
            <audio src={run.artifact.url.startsWith('http') ? run.artifact.url : `${process.env.REACT_APP_BACKEND_URL}${run.artifact.url}`}
              controls preload="metadata" className="w-full mt-1" data-testid="audio-preview" />
          )}
          {run.artifact.type === 'workforce_run' && run.artifact.summary && (
            <p className="text-sm text-zinc-300 whitespace-pre-wrap mt-1 max-h-56 overflow-y-auto" data-testid="run-summary">
              {run.artifact.summary}
            </p>
          )}
        </div>
      )}

      {/* Details — consumer-friendly only (no internal IDs/terminology) */}
      <button onClick={() => setShowDetails(!showDetails)}
        className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mt-2 transition-colors"
        data-testid="view-details-btn">
        {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {showDetails ? `Hide ${kindLabel.toLowerCase()} details` : `${kindLabel} details`}
      </button>
      {showDetails && (
        <div className="mt-1.5 text-xs text-zinc-400 space-y-0.5" data-testid="run-details">
          {run.details?.content_type && <p>{run.details.content_type}{run.details.platform ? ` · ${run.details.platform}` : ''}</p>}
          {run.details?.format && <p>Format: {run.details.format}</p>}
          {(run.artifact?.duration || run.details?.expected_duration) && (
            <p>Duration: ~{Math.round(run.artifact?.duration || run.details.expected_duration)}s</p>
          )}
          {run.details?.includes && <p>Includes: {run.details.includes}</p>}
          {run.details?.file_size && <p>File size: {(run.details.file_size / (1024 * 1024)).toFixed(1)} MB</p>}
          {run.started_at && <p>Started: {new Date(run.started_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}</p>}
          {run.status === 'completed'
            ? <p>Saved to your Content Library</p>
            : run.status === 'running' ? <p>Will be saved to your Content Library</p> : null}
        </div>
      )}
    </div>
  );
};

export default ActionRunCard;
